import { useState, useRef, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { toJpeg } from 'html-to-image';
import { useQuery } from '@tanstack/react-query';
import { DashboardLayout } from '@/components/DashboardLayout';
import { StatCard } from '@/components/StatCard';
import { ClientAvatar } from '@/components/ClientAvatar';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/contexts/AuthContext';
import { gymApi, adminApi, employeeApi, nutritionApi } from '@/lib/api';
import { DashboardStats, CheckIn, Client } from '@/types';
import { Button } from '@/components/ui/button';
import { DateRangePicker } from '@/components/DateRangePicker';
import { CheckInDateFilter } from '@/components/CheckInDateFilter';
import { getCheckInRange, toDateStr, CheckInDateMode } from '@/lib/checkin-date';
import {
  Activity,
  Clock,
  TrendingUp,
  TrendingDown,
  Users,
  Loader2,
  Building2,
  ShieldCheck,
  AlertTriangle,
  FileBarChart,
  Layout,
  MessageSquare,
  Download,
  QrCode,
  Share2,
  Copy,
  Dumbbell,
  ChevronDown,
  ChevronUp,
  ChevronRight,
  DollarSign,
  CreditCard,
  Eye,
  EyeOff
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { OnboardingChecklist } from '@/components/OnboardingChecklist';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import {
  Area,
  AreaChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer
} from 'recharts';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  ChartConfig
} from '@/components/ui/chart';

const chartConfig = {
  activity: {
    label: "Check-ins",
    color: "hsl(var(--primary))",
  },
} satisfies ChartConfig;

export default function DashboardPage() {
  const { user, gym, hasRole } = useAuth();
  const navigate = useNavigate();
  const [showRevenueBreakdown, setShowRevenueBreakdown] = useState(false);
  const [isCheckInsExpanded, setIsCheckInsExpanded] = useState(false);
  const [isRenewalsExpanded, setIsRenewalsExpanded] = useState(false);
  const [showRevenueAmount, setShowRevenueAmount] = useState(() => {
    const saved = localStorage.getItem(`show_revenue_${gym?.gym_id}`);
    return saved !== null ? JSON.parse(saved) : true;
  });

  const [dateFilter, setDateFilter] = useState<{ start: string; end: string } | null>(null);
  const [dateStart, setDateStart] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().split('T')[0];
  });
  const [dateEnd, setDateEnd] = useState(() => new Date().toISOString().split('T')[0]);

  const [checkInMode, setCheckInMode] = useState<CheckInDateMode>('hoy');
  const [checkInCustomRange, setCheckInCustomRange] = useState(() => {
    const today = toDateStr(new Date());
    return { start: today, end: today };
  });
  const checkInRange = getCheckInRange(checkInMode, checkInCustomRange);

  // Persist preference
  useEffect(() => {
    if (gym?.gym_id) {
      localStorage.setItem(`show_revenue_${gym.gym_id}`, JSON.stringify(showRevenueAmount));
    }
  }, [showRevenueAmount, gym?.gym_id]);

  const isSuperAdmin = hasRole(['super_admin']);
  const showSuperAdminDashboard = isSuperAdmin && !gym;

  // --- QUERIES ---

  // 1. Super Admin Stats
  const { data: superStats, isLoading: isLoadingSuper } = useQuery({
    queryKey: ['superAdminStats'],
    queryFn: async () => {
      const res = await adminApi.getSuperAdminStats(null);
      return res.success ? res.stats : null;
    },
    enabled: !!user && showSuperAdminDashboard,
  });

  // 2. Gym Stats
  const { data: stats, isLoading: isLoadingStats } = useQuery({
    queryKey: ['gymStats', gym?.gym_id, dateFilter?.start, dateFilter?.end, gym?.timezone],
    queryFn: async () => {
      // Sync memberships status first to ensure stats are accurate
      if (gym?.gym_id) {
        await gymApi.syncMemberships(gym.gym_id);
      }

      const res = await gymApi.getDashboardStats(null, gym?.gym_id, dateFilter?.start, dateFilter?.end, gym?.timezone);
      return res.success ? res.stats : null;
    },
    enabled: !!user && !!gym && !showSuperAdminDashboard,
  });

  // 3. Check-ins for the selected date range
  const { data: checkIns = [], isLoading: isLoadingCheckIns } = useQuery({
    queryKey: ['checkInsByRange', gym?.gym_id, checkInRange.start, checkInRange.end, gym?.timezone],
    queryFn: async () => {
      const res = await gymApi.listCheckInsByRange(null, gym?.gym_id, checkInRange.start, checkInRange.end, gym?.timezone);
      return res.success ? res.check_ins : [];
    },
    enabled: !!user && !!gym && !showSuperAdminDashboard,
  });

  // 4. Recent Check-ins (For collapsed view - top 5)
  const recentCheckIns = checkIns.slice(0, 5);

  // 5. Recent Renewals (last 8 days)
  const { data: recentRenewals = [], isLoading: isLoadingRenewals } = useQuery({
    queryKey: ['recentRenewals', gym?.gym_id],
    queryFn: async () => {
      const res = await gymApi.listRecentRenewals(null, gym?.gym_id);
      return res.success ? res.renewals : [];
    },
    enabled: !!user && !!gym && !showSuperAdminDashboard,
  });

  const recentRenewalsSlice = recentRenewals.slice(0, 5);

  // 6. Recent Clients
  const { data: recentClients = [], isLoading: isLoadingClients } = useQuery({
    queryKey: ['recentClients', gym?.gym_id],
    queryFn: async () => {
      const res = await gymApi.listClients(null, gym?.gym_id);
      return res.success ? res.clients.slice(0, 4) : [];
    },
    enabled: !!user && !!gym && !showSuperAdminDashboard,
  });

  // 5. Membership Plans (for onboarding task)
  const { data: plans = [] } = useQuery({
    queryKey: ['membershipPlans', gym?.gym_id],
    queryFn: async () => {
      const res = await gymApi.listMembershipPlans(gym?.gym_id);
      return res.success ? res.plans : [];
    },
    enabled: !!user && !!gym && !showSuperAdminDashboard,
  });

  // 6. Staff count (for onboarding task)
  const { data: employees = [] } = useQuery({
    queryKey: ['employeesCount', gym?.gym_id],
    queryFn: async () => {
      const res = await employeeApi.listEmployees(gym?.gym_id as string);
      return res.success ? res.employees : [];
    },
    enabled: !!user && !!gym && !showSuperAdminDashboard,
  });

  const isLoading = showSuperAdminDashboard ? isLoadingSuper : (isLoadingStats || isLoadingCheckIns || isLoadingClients);

  // 7. Métricas de citas de nutrición (solo si el add-on está activo)
  const hasNutrition = !!gym?.modules_enabled?.includes('nutricion');
  const { data: nutritionStats } = useQuery({
    queryKey: ['nutritionAppointmentStats', gym?.gym_id],
    queryFn: async () => {
      const res = await nutritionApi.getNutritionAppointmentStats(gym?.gym_id as string);
      return res.success ? res : null;
    },
    enabled: !!user && !!gym && !showSuperAdminDashboard && hasNutrition,
  });

  const getDayName = (dateStr: string) => {
    const days = ['Dom', 'Lun', 'Mar', 'Mie', 'Jue', 'Vie', 'Sab'];
    const date = new Date(dateStr + 'T00:00:00');
    return days[date.getDay()];
  };

  const chartData = stats?.weeklyActivity?.map(item => ({
    day: getDayName(item.date),
    count: item.count
  })) || [];

  const checkInTitle =
    checkInMode === 'hoy'
      ? 'Asistencia de Hoy'
      : checkInMode === 'semana'
      ? 'Asistencia de la Semana'
      : checkInMode === 'mes'
      ? 'Asistencia del Mes'
      : 'Asistencia en el Período';

  if (isLoading) {
    return (
      <DashboardLayout title="Dashboard">
        <div className="flex items-center justify-center h-[60vh]">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  // --- SUPER ADMIN DASHBOARD ---
  if (showSuperAdminDashboard) {
    return (
      <DashboardLayout
        title="Dashboard de Control"
        subtitle="Métricas globales del sistema"
      >
        {/* Super Admin Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <StatCard
            title="Total Gimnasios"
            value={superStats?.totalGyms || 0}
            icon="gym"
          />
          <StatCard
            title="Gimnasios Activos"
            value={superStats?.activeGyms || 0}
            icon="active"
            color="success"
          />
          <StatCard
            title="Por Vencer (30d)"
            value={superStats?.expiringSoon || 0}
            icon="expiring"
            color="warning"
          />
          <StatCard
            title="Gimnasios Vencidos"
            value={superStats?.expiredGyms || 0}
            icon="expired"
            color="destructive"
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Plan Distribution */}
          <Card className="lg:col-span-1">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileBarChart className="h-5 w-5 text-primary" />
                Suscripciones por Plan
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {Object.entries(superStats?.planDistribution || {}).map(([plan, count]: [string, any]) => (
                  <div key={plan} className="flex items-center justify-between">
                    <span className="capitalize font-medium">{plan}</span>
                    <Badge variant="outline" className="ml-2">{count}</Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* User Role Distribution */}
          <Card className="lg:col-span-1">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ShieldCheck className="h-5 w-5 text-primary" />
                Usuarios por Rol
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {Object.entries(superStats?.userRoles || {}).map(([role, count]: [string, any]) => (
                  <div key={role} className="flex items-center justify-between">
                    <span className="capitalize text-sm">{role.replace('_', ' ')}</span>
                    <Badge variant="secondary">{count}</Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Warning Card */}
          <Card className="lg:col-span-1 bg-muted/30">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-warning">
                <AlertTriangle className="h-5 w-5" />
                Gimnasios Inactivos
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold">{superStats?.inactiveGyms || 0}</p>
              <p className="text-muted-foreground text-sm mt-1">Requieren atención administrativa</p>
            </CardContent>
          </Card>
        </div>

        {/* Modules per Gym Summary */}
        <Card className="mt-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Layout className="h-5 w-5 text-primary" />
              Módulos Activos por Gimnasio
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {superStats?.gymModules?.map((gymMod: any) => (
                <div key={gymMod.name} className="p-4 border rounded-lg">
                  <p className="font-bold mb-2">{gymMod.name}</p>
                  <div className="flex flex-wrap gap-1">
                    {gymMod.modules?.map((m: string) => (
                      <Badge key={m} variant="info" className="text-[10px]">{m}</Badge>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </DashboardLayout>
    );
  }

  // --- STANDARD GYM DASHBOARD ---
  return (
    <DashboardLayout
      title="Dashboard"
      subtitle={gym?.name ? `Bienvenido a ${gym.name}` : isSuperAdmin ? 'Cargando datos del gimnasio...' : 'Bienvenido'}
    >
      {/* Stats Grid */}
      <div className="mb-6">
        <DateRangePicker
          startDate={dateStart}
          endDate={dateEnd}
          onStartChange={setDateStart}
          onEndChange={setDateEnd}
          onFilter={() => setDateFilter({ start: dateStart, end: dateEnd })}
          onReset={() => {
            setDateFilter(null);
            const d = new Date();
            d.setDate(d.getDate() - 30);
            setDateStart(d.toISOString().split('T')[0]);
            setDateEnd(new Date().toISOString().split('T')[0]);
          }}
          isLoading={isLoadingStats}
          isFiltered={!!dateFilter}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <StatCard
          title="Total Clientes"
          value={stats?.totalClients || 0}
          change={stats?.clientsGrowth || 0}
          icon="users"
          trendLabel={stats?.isFiltered ? 'en el período' : undefined}
        />
        <StatCard
          title="Miembros Activos"
          value={stats?.activeMembers || 0}
          change={stats?.membersGrowth || 0}
          icon="members"
          trendLabel={stats?.isFiltered ? 'en el período' : undefined}
        />
        <StatCard
          title={stats?.isFiltered ? 'Check-ins' : 'Check-ins Hoy'}
          value={stats?.todayCheckIns || 0}
          change={stats?.checkInsGrowth || 0}
          icon="checkins"
          trendLabel={stats?.isFiltered ? 'en el período' : 'vs ayer'}
        />
        {hasRole(['gym_admin', 'super_admin']) ? (
          <Card
            className={cn(
              "overflow-hidden hover:shadow-lg transition-all duration-500 cursor-pointer group relative",
              showRevenueBreakdown ? "ring-2 ring-success/20 shadow-xl" : "shadow-sm"
            )}
            onClick={() => setShowRevenueBreakdown(!showRevenueBreakdown)}
          >
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-bold text-muted-foreground uppercase tracking-wider">
                      {gym?.modules_enabled?.includes('inventory') ? "Ingresos Netos" : "Ingresos"}
                      {stats?.isFiltered ? " (filtrado)" : " del Mes"}
                    </p>
                    {showRevenueBreakdown ? <ChevronUp className="h-3 w-3 text-muted-foreground" /> : <ChevronDown className="h-3 w-3 text-muted-foreground" />}
                  </div>
                  <p className="text-3xl font-black text-foreground">
                    {showRevenueAmount ? (
                      `$${(gym?.modules_enabled?.includes('inventory') ? (stats?.netRevenue || 0) : (stats?.revenue || 0)).toLocaleString('es-ES', { minimumFractionDigits: 2 })}`
                    ) : (
                      "$ ••••••"
                    )}
                  </p>

                  {!stats?.isFiltered && (
                  <div className={cn(
                    "flex items-center gap-1 text-sm font-bold",
                    (stats?.revenueGrowth || 0) >= 0 ? "text-success" : "text-destructive"
                  )}>
                    {(stats?.revenueGrowth || 0) >= 0 ? <TrendingUp className="h-4 w-4" /> : <TrendingDown className="h-4 w-4" />}
                    <span>{Math.abs(stats?.revenueGrowth || 0)}%</span>
                    <span className="text-muted-foreground font-medium">vs mes anterior</span>
                  </div>
                  )}
                </div>

                <div className="flex flex-col items-end gap-2">
                  <div
                    className="rounded-[1.25rem] p-4 bg-success/10 text-success group-hover:scale-110 transition-transform duration-500"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowRevenueAmount(!showRevenueAmount);
                    }}
                  >
                    {showRevenueAmount ? <Eye className="h-6 w-6" /> : <EyeOff className="h-6 w-6" />}
                  </div>
                </div>
              </div>

              {/* Collapsible Content */}
              {showRevenueBreakdown && (
                <div className="mt-6 pt-6 border-t border-dashed border-border/50 animate-in slide-in-from-top-2 fade-in duration-500 space-y-3">
                  <div className="flex justify-between items-center bg-muted/50 p-2.5 rounded-xl">
                    <span className="text-xs font-bold text-muted-foreground uppercase">Membresías</span>
                    <span className="text-sm font-black text-foreground">
                      {showRevenueAmount ? `$${(stats?.membershipRevenue || 0).toLocaleString('es-ES', { minimumFractionDigits: 2 })}` : "$ •••"}
                    </span>
                  </div>
                  {gym?.modules_enabled?.includes('inventory') && (
                    <>
                      <div className="flex justify-between items-center bg-muted/50 p-2.5 rounded-xl">
                        <span className="text-xs font-bold text-muted-foreground uppercase">Ventas (POS)</span>
                        <span className="text-sm font-black text-foreground">
                          {showRevenueAmount ? `$${(stats?.salesRevenue || 0).toLocaleString('es-ES', { minimumFractionDigits: 2 })}` : "$ •••"}
                        </span>
                      </div>
                      <div className="flex justify-between items-center bg-destructive/5 p-2.5 rounded-xl border border-destructive/5">
                        <span className="text-xs font-bold text-destructive/70 uppercase">Costos de Inventario</span>
                        <span className="text-sm font-black text-destructive/70">
                          {showRevenueAmount ? `-$${(stats?.inventoryExpenses || 0).toLocaleString('es-ES', { minimumFractionDigits: 2 })}` : "-$ •••"}
                        </span>
                      </div>
                    </>
                  )}
                  <div className="flex justify-between items-center bg-destructive/5 p-2.5 rounded-xl border border-destructive/5">
                    <span className="text-xs font-bold text-destructive/70 uppercase">Gastos Generales</span>
                    <span className="text-sm font-black text-destructive/70">
                      {showRevenueAmount ? `-$${(stats?.generalExpenses || 0).toLocaleString('es-ES', { minimumFractionDigits: 2 })}` : "-$ •••"}
                    </span>
                  </div>
                  <div className="flex justify-between items-center bg-success/5 p-2.5 rounded-xl border border-success/20">
                    <span className="text-xs font-bold text-success uppercase">Utilidad Neta</span>
                    <span className="text-sm font-black text-success">
                      {showRevenueAmount ? `$${(stats?.netRevenue || 0).toLocaleString('es-ES', { minimumFractionDigits: 2 })}` : "$ •••"}
                    </span>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        ) : (
          <StatCard
            title="Actividad de Red"
            value={stats?.todayCheckIns || 0}
            icon="activity"
          />
        )}
      </div>

      {/* Citas de nutrición (add-on) */}
      {!showSuperAdminDashboard && hasNutrition && nutritionStats?.success && (
        <Card
          className="mb-8 cursor-pointer border-l-4 border-l-orange-500 transition-all hover:scale-[1.01]"
          onClick={() => navigate('/nutrition?tab=agenda')}
        >
          <CardContent className="pt-6">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div>
                <p className="mb-1 text-[10px] font-black uppercase tracking-widest text-muted-foreground">
                  Citas de nutrición
                </p>
                <h3 className="text-2xl font-black">
                  {nutritionStats.upcoming_7d} próxima(s) en 7 días
                </h3>
                <p className="text-xs text-muted-foreground">
                  {nutritionStats.today} hoy · {nutritionStats.month_total} este mes ·{' '}
                  {nutritionStats.month_from_public} reservadas en línea
                </p>
              </div>
              <div className="flex items-center gap-2 text-sm font-bold text-orange-500">
                Ver agenda <ChevronRight className="h-4 w-4" />
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Critical Alerts Row */}
      {!showSuperAdminDashboard && stats && (stats.expiringToday > 0 || stats.expiringSoon > 0 || stats.lowSessions > 0) && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <Card 
            className={cn("cursor-pointer border-l-4 transition-all hover:scale-[1.01]", stats.expiringToday > 0 ? "border-l-destructive shadow-md" : "border-l-muted opacity-60")}
            onClick={() => {
              if (stats.expiringToday > 0) navigate('/memberships?status=active');
            }}
          >
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[10px] font-black uppercase tracking-widest text-muted-foreground mb-1">Vencen Hoy</p>
                  <h3 className="text-2xl font-black">{stats.expiringToday}</h3>
                </div>
                <div className={cn("p-2.5 rounded-xl", stats.expiringToday > 0 ? "bg-destructive/10 text-destructive" : "bg-muted text-muted-foreground")}>
                  <AlertTriangle className="h-5 w-5" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card 
            className={cn("cursor-pointer border-l-4 transition-all hover:scale-[1.01]", stats.expiringSoon > 0 ? "border-l-orange-500 shadow-md" : "border-l-muted opacity-60")}
            onClick={() => {
              if (stats.expiringSoon > 0) navigate('/memberships?status=active');
            }}
          >
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[10px] font-black uppercase tracking-widest text-muted-foreground mb-1">Vencen pronto (3d)</p>
                  <h3 className="text-2xl font-black">{stats.expiringSoon}</h3>
                </div>
                <div className={cn("p-2.5 rounded-xl", stats.expiringSoon > 0 ? "bg-orange-500/10 text-orange-500" : "bg-muted text-muted-foreground")}>
                  <Clock className="h-5 w-5" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card 
            className={cn("cursor-pointer border-l-4 transition-all hover:scale-[1.01]", stats.lowSessions > 0 ? "border-l-primary shadow-md" : "border-l-muted opacity-60")}
            onClick={() => {
              if (stats.lowSessions > 0) navigate('/memberships?status=active');
            }}
          >
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[10px] font-black uppercase tracking-widest text-muted-foreground mb-1">Bajas Sesiones (≤2)</p>
                  <h3 className="text-2xl font-black">{stats.lowSessions}</h3>
                </div>
                <div className={cn("p-2.5 rounded-xl", stats.lowSessions > 0 ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground")}>
                  <Activity className="h-5 w-5" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Share Registration URL Card */}
      {gym && (
        <Card className="mb-8 border-none shadow-xl overflow-hidden glass group hover:shadow-glow transition-all duration-500">
          <CardContent className="p-0">
            <div className="flex flex-col md:flex-row items-stretch">
              <div className="p-6 md:p-8 flex items-center gap-6 flex-1">
                <div className="h-16 w-16 rounded-[1.5rem] bg-primary/10 flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform duration-500">
                  <Share2 className="h-8 w-8 text-primary" />
                </div>
                <div>
                  <h3 className="text-xl font-black mb-1">Comparte tu Gimnasio</h3>
                  <p className="text-sm text-slate-500 dark:text-slate-400 font-medium">
                    Copia el enlace de inscripción para tus redes sociales o campañas.
                  </p>
                </div>
              </div>
              <div className="px-6 md:px-8 py-6 bg-slate-50/50 dark:bg-white/5 flex items-center gap-3 border-t md:border-t-0 md:border-l border-slate-100 dark:border-white/10">
                <div className="flex-1 md:w-72 px-4 py-3 bg-white dark:bg-slate-900 rounded-xl text-sm font-mono truncate border border-slate-200 dark:border-slate-800 text-slate-600 dark:text-slate-300">
                  {`${window.location.origin}/join/${gym.slug || 'registro'}`}
                </div>
                <div className="flex gap-2">
                  <Dialog>
                    <DialogTrigger asChild>
                      <Button
                        className="h-12 w-12 rounded-xl border border-slate-200 dark:border-white/10 bg-white dark:bg-slate-900 shadow-sm hover:bg-slate-50 dark:hover:bg-white/5 transition-all text-primary"
                        size="icon"
                        variant="outline"
                      >
                        <QrCode className="h-5 w-5" />
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-[95vw] sm:max-w-[450px] p-0 border-none bg-transparent shadow-none max-h-[95vh] overflow-y-auto overflow-x-hidden custom-scrollbar">
                      <Tabs defaultValue="join" className="w-full overflow-hidden">
                        <div className="bg-slate-900/90 backdrop-blur-md p-2 flex justify-center border-b border-white/10">
                          <TabsList className="bg-white/5 border border-white/10">
                            <TabsTrigger value="join" className="data-[state=active]:bg-primary data-[state=active]:text-white uppercase text-[10px] font-black tracking-widest px-6">Inscripción</TabsTrigger>
                            <TabsTrigger value="checkin" className="data-[state=active]:bg-primary data-[state=active]:text-white uppercase text-[10px] font-black tracking-widest px-6">Asistencia</TabsTrigger>
                          </TabsList>
                        </div>

                        {['join', 'checkin'].map((mode) => (
                          <TabsContent key={mode} value={mode} className="m-0 focus-visible:ring-0 overflow-hidden">
                            <div
                              id={`poster-content-${mode}`}
                              className="bg-white p-4 sm:p-8 relative overflow-hidden flex flex-col items-center min-h-[450px] sm:min-h-[600px] border-[6px] sm:border-[12px] border-slate-100"
                            >
                              {/* Decorative Grid Dots */}
                              <div className="absolute top-4 left-4 grid grid-cols-4 gap-2 opacity-20">
                                {[...Array(16)].map((_, i) => <div key={i} className="w-1.5 h-1.5 bg-black rounded-full" />)}
                              </div>
                              <div className="absolute top-4 right-4 grid grid-cols-4 gap-2 opacity-20">
                                {[...Array(16)].map((_, i) => <div key={i} className="w-1.5 h-1.5 bg-black rounded-full" />)}
                              </div>
                              <div className="absolute bottom-4 right-4 grid grid-cols-4 gap-2 opacity-20">
                                {[...Array(16)].map((_, i) => <div key={i} className="w-1.5 h-1.5 bg-black rounded-full" />)}
                              </div>

                              {/* Corner X Marks */}
                              <div className="absolute top-32 right-6 flex flex-col gap-2 opacity-60">
                                {[...Array(4)].map((_, i) => <span key={i} className="text-xl font-black leading-none">×</span>)}
                              </div>
                              <div className="absolute bottom-32 left-6 flex flex-col gap-2 opacity-60">
                                {[...Array(4)].map((_, i) => <span key={i} className="text-xl font-black leading-none">×</span>)}
                              </div>

                              {/* Title Section */}
                              <div className="mt-8 text-center relative z-10 w-full px-2">
                                <h2 className="text-[3rem] sm:text-[4rem] font-[1000] text-black tracking-tighter uppercase leading-[0.85] mb-2 [text-shadow:2px_2px_0px_#ccc]">
                                  ESCANÉAME
                                </h2>
                                <div className="flex justify-center mt-4 sm:mt-6">
                                  <p className="text-[10px] sm:text-sm font-black tracking-[0.2em] uppercase text-black border-y-[2px] sm:border-y-[3px] border-black inline-block px-3 py-1 sm:px-4 sm:py-1.5">
                                    {mode === 'join' ? 'PARA INSCRIBIRTE' : 'PARA REPORTAR ASISTENCIA'}
                                  </p>
                                </div>
                              </div>

                              {/* QR Code Container */}
                              <div className="my-8 relative z-10 p-3 border-[3px] border-black rounded-[2rem]">
                                <div className="bg-white p-1">
                                  <img
                                    src={`https://quickchart.io/qr?text=${encodeURIComponent(`${window.location.origin}/${mode}/${gym.slug || (mode === 'join' ? 'registro' : 'asistencia')}`)}&size=300&margin=0&ecLevel=H`}
                                    alt="QR Code"
                                    crossOrigin="anonymous"
                                    className="w-48 h-48 sm:w-56 sm:h-56 grayscale contrast-[1.5]"
                                  />
                                </div>
                              </div>

                              {/* Footer Gym Name */}
                              <div className="mt-auto w-full max-w-[240px] sm:max-w-[280px] relative z-10 mb-4 text-center">
                                <div className="border-[2px] sm:border-[3px] border-black rounded-full py-2 px-4 sm:py-3 sm:px-6 bg-white shadow-[3px_3px_0px_0px_rgba(0,0,0,1)] sm:shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] inline-block">
                                  <span className="text-sm sm:text-lg font-black uppercase tracking-widest text-black italic truncate max-w-[180px] sm:max-w-none block">
                                    {gym.name}
                                  </span>
                                </div>
                              </div>
                            </div>

                            <div className="px-4 py-6 bg-slate-900 flex flex-col gap-3">
                              <Button
                                className="h-12 rounded-xl gradient-primary text-white font-bold flex items-center justify-center gap-2"
                                onClick={async () => {
                                  const element = document.getElementById(`poster-content-${mode}`);
                                  if (!element) return;

                                  const loadingToast = toast.loading('Generando póster de alta calidad...');

                                  try {
                                    // Set scale for high quality
                                    const dataUrl = await toJpeg(element, {
                                      quality: 0.95,
                                      pixelRatio: 2, // Double resolution for HD
                                      backgroundColor: '#ffffff'
                                    });

                                    const link = document.createElement('a');
                                    link.download = `poster-${mode}-${gym.name.toLowerCase().replace(/\s+/g, '-')}.jpg`;
                                    link.href = dataUrl;
                                    link.click();

                                    toast.dismiss(loadingToast);
                                    toast.success(`Póster de ${mode === 'join' ? 'Inscripción' : 'Asistencia'} descargado`);
                                  } catch (error) {
                                    toast.dismiss(loadingToast);
                                    console.error('Download error:', error);
                                    toast.error('Error al generar la imagen. Inténtalo de nuevo.');
                                  }
                                }}
                              >
                                <Download className="h-5 w-5" />
                                <span>Descargar Póster (HD)</span>
                              </Button>
                              <Button
                                variant="outline"
                                className="h-12 rounded-xl border-white/10 bg-white/5 text-white flex items-center justify-center gap-2"
                                onClick={() => {
                                  const path = mode === 'join' ? 'join' : 'checkin';
                                  const slug = gym.slug || (mode === 'join' ? 'registro' : 'asistencia');
                                  navigator.clipboard.writeText(`${window.location.origin}/${path}/${slug}`);
                                  toast.success("Enlace copiado");
                                }}
                              >
                                <Copy className="h-5 w-5" />
                                <span>Copiar Enlace Directo</span>
                              </Button>
                            </div>
                          </TabsContent>
                        ))}
                      </Tabs>
                    </DialogContent>
                  </Dialog>
                  <Button
                    className="h-12 w-12 rounded-xl gradient-primary shadow-lg hover:scale-105 transition-all text-white"
                    size="icon"
                    onClick={() => {
                      navigator.clipboard.writeText(`${window.location.origin}/join/${gym.slug || 'registro'}`);
                      toast.success("Enlace copiado con éxito");
                    }}
                  >
                    <Copy className="h-5 w-5" />
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Charts and Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Activity Chart Placeholder */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-primary" />
              Actividad Semanal
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[300px] w-full mt-4">
              {chartData.length > 0 ? (
                <ChartContainer config={chartConfig} className="h-full w-full">
                  <AreaChart
                    data={chartData}
                    margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
                  >
                    <defs>
                      <linearGradient id="colorActivity" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="var(--color-activity)" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="var(--color-activity)" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid
                      strokeDasharray="3 3"
                      vertical={false}
                      stroke="currentColor"
                      className="opacity-[0.05]"
                    />
                    <XAxis
                      dataKey="day"
                      axisLine={false}
                      tickLine={false}
                      tick={{ fill: 'currentColor', fontSize: 12 }}
                      className="opacity-50"
                      dy={10}
                    />
                    <YAxis
                      axisLine={false}
                      tickLine={false}
                      tick={{ fill: 'currentColor', fontSize: 12 }}
                      className="opacity-50"
                      dx={-10}
                    />
                    <ChartTooltip
                      content={<ChartTooltipContent />}
                    />
                    <Area
                      type="monotone"
                      dataKey="count"
                      stroke="var(--color-activity)"
                      strokeWidth={3}
                      fillOpacity={1}
                      fill="url(#colorActivity)"
                      animationDuration={1500}
                    />
                  </AreaChart>
                </ChartContainer>
              ) : (
                <div className="h-full flex flex-col items-center justify-center rounded-lg bg-muted/50 border-2 border-dashed border-muted">
                  <Activity className="h-12 w-12 text-muted-foreground/30 mb-2" />
                  <p className="text-muted-foreground font-medium">Sin datos esta semana</p>
                  <p className="text-xs text-muted-foreground/60">Registra check-ins para ver actividad</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Recent Check-ins */}
        <Card className={cn(
          "transition-all duration-500",
          isCheckInsExpanded && "lg:col-span-3 order-first lg:order-none"
        )}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-primary" />
              {checkInTitle}
            </CardTitle>
            <CheckInDateFilter
              mode={checkInMode}
              onModeChange={(mode) => {
                setCheckInMode(mode);
                setIsCheckInsExpanded(false);
              }}
              start={checkInCustomRange.start}
              end={checkInCustomRange.end}
              onStartChange={(start) => setCheckInCustomRange((prev) => ({ ...prev, start }))}
              onEndChange={(end) => setCheckInCustomRange((prev) => ({ ...prev, end }))}
            />
          </CardHeader>
          <CardContent>
            <div className={cn(
              "space-y-4 transition-all duration-500",
              isCheckInsExpanded ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 space-y-0" : "flex flex-col"
            )}>
              {(isCheckInsExpanded ? checkIns : recentCheckIns).map((checkin) => (
                <div
                  key={checkin.checkin_id}
                  className="flex items-center justify-between p-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors border border-transparent hover:border-primary/10"
                >
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <span className="text-sm font-medium text-primary">
                        {checkin.client_name?.split(' ').map(n => n[0]).join('')}
                      </span>
                    </div>
                    <div>
                      <Link
                        to={`/memberships?search=${encodeURIComponent(checkin.client_name || '')}&clientId=${encodeURIComponent(checkin.client_id)}`}
                        className="hover:text-primary transition-colors cursor-pointer flex items-center gap-2"
                      >
                        <p className="font-bold text-sm">{checkin.client_name}</p>
                        <Badge variant="outline" className={cn(
                          "text-[9px] px-1.5 py-0 h-4 uppercase font-black tracking-tighter",
                          checkin.method === 'self_checkin' ? "border-blue-500 text-blue-500 bg-blue-50/50" : "border-primary text-primary bg-primary/5"
                        )}>
                          {checkin.method === 'self_checkin' ? 'SOCIO' : 'ADMIN'}
                        </Badge>
                      </Link>
                      <div className="flex items-center gap-2 mt-0.5">
                        <p className="text-[10px] text-muted-foreground font-medium">
                          {checkInMode === 'hoy'
                            ? new Date(checkin.timestamp).toLocaleTimeString('es-ES', {
                                hour: '2-digit',
                                minute: '2-digit'
                              })
                            : new Date(checkin.timestamp).toLocaleString('es-ES', {
                                day: '2-digit',
                                month: 'short',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                        </p>
                        {checkin.result === 'denied' && checkin.reason && (
                          <span className="text-[10px] text-destructive font-black uppercase tracking-tighter animate-pulse">• {checkin.reason}</span>
                        )}
                        {checkin.sessions_remaining !== undefined && checkin.sessions_remaining !== null && (
                          <span className="text-[10px] text-muted-foreground font-bold">• {checkin.sessions_remaining} ses. rest.</span>
                        )}
                      </div>
                    </div>
                  </div>
                  <Badge variant={checkin.result === 'allowed' ? 'success' : 'destructive'} className="font-black text-[10px]">
                    {checkin.result === 'allowed' ? 'OK' : 'DENIED'}
                  </Badge>
                </div>
              ))}
              {(isCheckInsExpanded ? checkIns : recentCheckIns).length === 0 && (
                <div className="col-span-full py-12 text-center">
                  <Clock className="h-12 w-12 text-muted-foreground/20 mx-auto mb-3" />
                  <p className="text-sm text-muted-foreground">No hay registros de asistencia en el período</p>
                </div>
              )}
            </div>

            {checkIns.length > 5 && (
              <Button
                variant="ghost"
                className="w-full mt-6 text-primary hover:text-primary hover:bg-primary/5 font-bold uppercase tracking-widest text-[10px]"
                onClick={() => setIsCheckInsExpanded(!isCheckInsExpanded)}
              >
                {isCheckInsExpanded ? (
                  <>
                    <ChevronUp className="h-4 w-4 mr-2" />
                    Ver Menos
                  </>
                ) : (
                  <>
                    <ChevronDown className="h-4 w-4 mr-2" />
                    Ver Todos ({checkIns.length})
                  </>
                )}
              </Button>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Recent Renewals */}
      <Card className="mt-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="h-5 w-5 text-emerald-500" />
            Renovaciones Recientes
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoadingRenewals ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <>
              <div className={cn(
                "space-y-4 transition-all duration-500",
                isRenewalsExpanded ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 space-y-0" : "flex flex-col"
              )}>
                {(isRenewalsExpanded ? recentRenewals : recentRenewalsSlice).map((renewal) => {
                  const paidDate = new Date(renewal.paid_at);
                  const methodLabel = renewal.payment_method === 'cash' ? 'Efectivo' : renewal.payment_method === 'card' ? 'Tarjeta' : 'Transferencia';
                  const methodColor = renewal.payment_method === 'cash'
                    ? 'border-emerald-500 text-emerald-600 bg-emerald-50/50'
                    : renewal.payment_method === 'card'
                    ? 'border-blue-500 text-blue-600 bg-blue-50/50'
                    : 'border-purple-500 text-purple-600 bg-purple-50/50';

                  return (
                    <div
                      key={renewal.id}
                      className="flex items-center justify-between p-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors border border-transparent hover:border-emerald-500/10"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="h-10 w-10 rounded-full bg-emerald-500/10 flex items-center justify-center shrink-0">
                          <span className="text-sm font-medium text-emerald-600">
                            {renewal.client_name?.split(' ').map((n: string) => n[0]).join('')}
                          </span>
                        </div>
                        <div className="min-w-0">
                          <Link
                            to={`/memberships?search=${encodeURIComponent(renewal.client_name || '')}&clientId=${encodeURIComponent(renewal.client_id)}`}
                            className="hover:text-primary transition-colors"
                          >
                            <p className="font-bold text-sm truncate">{renewal.client_name}</p>
                          </Link>
                          <div className="flex items-center gap-2 mt-0.5">
                            <p className="text-[10px] text-muted-foreground">{renewal.type}</p>
                            <span className="text-[10px] font-bold text-foreground">${renewal.price.toFixed(2)}</span>
                          </div>
                          <div className="flex items-center gap-2 mt-0.5">
                            <Badge variant="outline" className={cn("text-[9px] px-1.5 py-0 h-4 uppercase font-black tracking-tighter", methodColor)}>
                              {methodLabel}
                            </Badge>
                            <p className="text-[10px] text-muted-foreground font-medium">
                              {paidDate.toLocaleDateString('es-ES', { day: '2-digit', month: 'short' })}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
                {(isRenewalsExpanded ? recentRenewals : recentRenewalsSlice).length === 0 && (
                  <div className="col-span-full py-12 text-center">
                    <CreditCard className="h-12 w-12 text-muted-foreground/20 mx-auto mb-3" />
                    <p className="text-sm text-muted-foreground">No hay renovaciones en los últimos 8 días</p>
                  </div>
                )}
              </div>

              {recentRenewals.length > 5 && (
                <Button
                  variant="ghost"
                  className="w-full mt-6 text-primary hover:text-primary hover:bg-primary/5 font-bold uppercase tracking-widest text-[10px]"
                  onClick={() => setIsRenewalsExpanded(!isRenewalsExpanded)}
                >
                  {isRenewalsExpanded ? (
                    <>
                      <ChevronUp className="h-4 w-4 mr-2" />
                      Ver Menos
                    </>
                  ) : (
                    <>
                      <ChevronDown className="h-4 w-4 mr-2" />
                      Ver Todos ({recentRenewals.length})
                    </>
                  )}
                </Button>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Recent Clients */}
      <Card className="mt-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5 text-primary" />
            Clientes Recientes
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {recentClients.map((client) => (
              <div
                key={client.client_id}
                className="p-4 rounded-xl bg-muted/50 hover:bg-muted transition-colors"
              >
                <div className="flex items-center gap-3 mb-3">
                  <ClientAvatar name={client.name} photoPath={client.photo_path} className="h-12 w-12" textClassName="text-lg" />
                  <div>
                    <p className="font-medium">{client.name}</p>
                    <Badge variant={client.status === 'active' ? 'success' : 'muted'} className="mt-1">
                      {client.status === 'active' ? 'Activo' : 'Inactivo'}
                    </Badge>
                  </div>
                </div>
                <p className="text-sm text-muted-foreground truncate">{client.email}</p>
                <p className="text-sm text-muted-foreground">{client.phone}</p>
              </div>
            ))}
            {recentClients.length === 0 && (
              <div className="col-span-full py-8 text-center text-muted-foreground">
                No hay clientes registrados aún
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Onboarding Checklist for new users */}
      {
        !showSuperAdminDashboard && stats && (
          <OnboardingChecklist
            stats={{
              totalClients: stats.totalClients,
              hasMembershipPlans: plans.length > 0,
              todayCheckIns: stats.todayCheckIns,
              totalCheckIns: stats.totalCheckIns,
              staffCount: employees.length,
            }}
            gymId={gym?.gym_id}
            gymSettings={{
              logo: gym?.logo,
              phone: gym?.phone,
              country: gym?.country,
              city: gym?.city,
              modules_enabled: gym?.modules_enabled,
            }}
          />
        )
      }
    </DashboardLayout >
  );
}
